import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardHeader,
  CardBody,
  Icon,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { WarningTwoIcon, CheckCircleIcon } from '@chakra-ui/icons';

const carveOutData = [
  {
    id: 'CLM-5540-E',
    patient: "Kevin O'Connell",
    dos: '2026-07-21',
    billedCode: '90837',
    description: 'Psychotherapy, 60 minutes',
    originalPlan: 'Keystone First (PH-MCO)',
    status: 'Flagged',
    carveOutRule: 'PA HealthChoices BH carve-out: behavioral health services are administered by the county BH-MCO, not the physical-health MCO. Patient county: Philadelphia \u2192 bill Community Behavioral Health (CBH) as an FQHC BH encounter.',
    ffsRate: '$186.00',
    action: 'Re-route to CBH'
  },
  {
    id: 'CLM-8832-B',
    patient: 'James Wilson',
    dos: '2026-07-09',
    billedCode: 'H0020',
    description: 'Methadone administration and/or service',
    originalPlan: 'Keystone First (PH-MCO)',
    status: 'Flagged',
    carveOutRule: 'Substance use disorder services, including NTP/methadone administration, fall under the BH carve-out. County determines the BH-MCO \u2014 CBH in Philadelphia; Magellan in several surrounding counties. Never billable to the PH-MCO.',
    ffsRate: '$18.52',
    action: 'Re-route to BH-MCO'
  }
];

const FqPaDemoCarveOutAnalyzer = () => {
  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg" color="brand.700" mb={2}>HealthChoices Carve-Out Analyzer</Heading>
          <Text color="gray.600">
            Detecting services billed to the wrong side of the PA HealthChoices carve-out \u2014 behavioral health belongs to the county BH-MCO, not the physical-health MCO.
          </Text>
        </Box>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Intelligence Layer Active</AlertTitle>
            <AlertDescription display="block">
              Revedy has cross-referenced incoming claims against the PA FQHC PROMISe Provider Handbook, the HealthChoices BH carve-out rules, and the county-to-BH-MCO crosswalk (CBH, Magellan, et al.).
            </AlertDescription>
          </Box>
        </Alert>

        <Card variant="outline" bg="white">
          <CardHeader bg="gray.50" borderBottomWidth="1px">
            <HStack justify="space-between">
              <Heading size="md">Flagged Carve-Out Opportunities</Heading>
              <Badge colorScheme="orange" p={2} borderRadius="md">2 Claims Require Action</Badge>
            </HStack>
          </CardHeader>
          <CardBody p={0}>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Claim ID / Patient</Th>
                  <Th>Service Code</Th>
                  <Th>Original Plan</Th>
                  <Th>Revedy Intelligence</Th>
                  <Th isNumeric>Expected Rate</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {carveOutData.map((claim, idx) => (
                  <Tr key={idx}>
                    <Td>
                      <Text fontWeight="bold">{claim.id}</Text>
                      <Text fontSize="sm" color="gray.500">{claim.patient}</Text>
                      <Text fontSize="xs" color="gray.400">DOS: {claim.dos}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple" mb={1}>{claim.billedCode}</Badge>
                      <Text fontSize="xs" noOfLines={2} maxW="150px">{claim.description}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="gray">{claim.originalPlan}</Badge>
                    </Td>
                    <Td maxW="300px">
                      <HStack align="start">
                        <Icon as={WarningTwoIcon} color="orange.500" mt={1} />
                        <Text fontSize="sm" color="gray.700">
                          {claim.carveOutRule}
                        </Text>
                      </HStack>
                    </Td>
                    <Td isNumeric>
                      <Text fontWeight="bold" color="green.600">{claim.ffsRate}</Text>
                      <Text fontSize="xs" color="gray.500">BH-MCO / PPS</Text>
                    </Td>
                    <Td>
                      <Button size="sm" colorScheme="blue" leftIcon={<CheckCircleIcon />}>
                        {claim.action}
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default FqPaDemoCarveOutAnalyzer;
